import React from 'react';
import { Link, useLocation } from 'react-router-dom';import { useMenu } from '../../../shared/hooks/useMenu.js';
import { ROUTE_REGISTRY } from './RouteRegistry.js';
import { SectionIcon } from './icons.js';
import type { MenuSection } from '../../../shared/hooks/useMenu.js';
import { useAuth } from '../auth/AuthProvider.js';

export interface SidebarProps {
  readonly collapsed: boolean;
  readonly onClose?: () => void;
}

const OPEN_SECTIONS_KEY = 'tl.sidebar.openSections';

const SECTION_LABELS: Record<string, string> = {
  OVERVIEW: 'Overview',
  ADMIN: 'Administration',
  SALES: 'Sales',
  BO: 'Back Office',
  VENDOR: 'Vendor',
  // Section code stays PAINTER (menu data keys off it); the wording is the
  // business term the product owner uses.
  PAINTER: 'Field Force',
  QUALITY: 'Quality',
};

function fallbackSections(): MenuSection[] {
  const map = new Map<string, { code: string; label: string; items: { id: string; code: string; label: string; path: string; icon: string; section: string; sortOrder: number; isVisible: boolean }[] }>();
  for (const r of ROUTE_REGISTRY) {
    const sec = map.get(r.section) ?? {
      code: r.section,
      label: SECTION_LABELS[r.section] ?? r.section,
      items: [],
    };
    sec.items.push({
      id: r.path,
      code: r.path,
      label: r.label,
      path: r.path,
      icon: r.icon ?? '',
      section: r.section,
      sortOrder: r.sortOrder,
      isVisible: true,
    });
    map.set(r.section, sec);
  }
  return Array.from(map.values()).map((s) => ({
    ...s,
    items: [...s.items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
  }));
}

export function Sidebar({ collapsed, onClose }: SidebarProps): React.ReactElement {
  const { token, logout, user } = useAuth();
  const { menu, loading, error, unauthorized } = useMenu('/api', token, 'web');
  const loc = useLocation();

  // Super Admin administers the platform; operational screens belong to the
  // Tenant Admin. Those entries are coloured, never hidden or blocked.
  const isPlatformAdmin = (user?.roles ?? []).some((r) => r.toUpperCase() === 'SUPER_ADMIN');

  // An expired token is not an outage — end the session so RequireAuth routes
  // back to sign-in, rather than leaving a stale shell over a dead session.
  React.useEffect(() => {
    if (unauthorized) logout();
  }, [unauthorized, logout]);

  // ROUTE_REGISTRY is a last-resort offline menu, not the admin-configured one.
  // Showing it while /me/menu is still in flight makes the sidebar visibly swap
  // lists after login and briefly exposes pages the admin may have revoked.
  const sections: MenuSection[] =
    menu && menu.sections.length > 0 ? [...menu.sections] : error && !unauthorized ? fallbackSections() : [];

  // Collapsed-by-default keeps the list short; the section holding the current
  // page is always open so the user can see where they are.
  const activeSection = React.useMemo(
    () => sections.find((s) => s.items.some((i) => i.path === loc.pathname))?.code ?? null,
    [sections, loc.pathname],
  );

  const [openSections, setOpenSections] = React.useState<Record<string, boolean>>(() => {
    try {
      const raw = window.localStorage.getItem(OPEN_SECTIONS_KEY);
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });

  React.useEffect(() => {
    try {
      window.localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(openSections));
    } catch {
      /* private mode / quota — remembering the state is a convenience, not a requirement */
    }
  }, [openSections]);

  const toggleSection = React.useCallback((code: string) => {
    setOpenSections((prev) => ({ ...prev, [code]: !prev[code] }));
  }, []);

  const isOpen = (code: string): boolean => openSections[code] ?? code === activeSection;

  return (
    <nav
      role="navigation"
      aria-label="sidebar"
      data-testid="sidebar"
      data-collapsed={collapsed}
      className={collapsed ? 'tl-sidebar tl-sidebar--collapsed no-print' : 'tl-sidebar no-print'}
    >
      <div className="tl-sidebar__inner">
        {loading && (
          <div data-testid="sidebar-loading" className="tl-sidebar__skeleton" aria-busy="true" aria-label="Loading menu">
            {Array.from({ length: 3 }, (_, g) => (
              <div key={g} className="tl-navgroup">
                <div className="tl-skel tl-skel--label" />
                {Array.from({ length: 5 }, (_, i) => (
                  <div key={i} className="tl-skel tl-skel--item" />
                ))}
              </div>
            ))}
          </div>
        )}
        {error && !unauthorized && !collapsed && (
          <div data-testid="sidebar-error" role="alert" className="tl-sidebar__notice">
            Using offline menu (server unavailable)
          </div>
        )}
        {sections.map((sec) => {
          const expanded = isOpen(sec.code);
          return (
          <div key={sec.code} data-testid={`sidebar-section-${sec.code}`} className="tl-navgroup">
            <button
              type="button"
              className="tl-navgroup__label tl-navgroup__toggle"
              data-testid={`sidebar-section-toggle-${sec.code}`}
              aria-expanded={expanded}
              aria-controls={`sidebar-list-${sec.code}`}
              onClick={() => toggleSection(sec.code)}
              title={expanded ? `Collapse ${sec.label}` : `Expand ${sec.label}`}
            >
              <SectionIcon code={sec.code} size={14} />
              <span className="tl-navgroup__text">{sec.label}</span>
              <span className="tl-navgroup__count" aria-hidden="true">{sec.items.length}</span>
              <span className="tl-navgroup__chevron" aria-hidden="true">{expanded ? '\u2212' : '+'}</span>
            </button>
            <ul
              className="tl-navlist"
              id={`sidebar-list-${sec.code}`}
              hidden={!expanded}
            >
              {sec.items.map((it) => {
                const active = loc.pathname === it.path;
                const mono = (it.label.trim()[0] ?? '?').toUpperCase();
                // Flagged, not blocked: a platform administrator should not be
                // holding tenant business screens. Access is unchanged.
                const flagged = isPlatformAdmin && it.governanceScope === 'OPERATIONAL';
                return (
                  <li key={it.id}>
                    <Link
                      to={it.path}
                      data-testid={`sidebar-item-${it.id}`}
                      data-governance={flagged ? 'operational' : undefined}
                      onClick={onClose}
                      title={flagged ? `${it.label} — operational screen, not for a platform administrator` : it.label}
                      aria-current={active ? 'page' : undefined}
                      className={active ? 'tl-navitem tl-navitem--active' : 'tl-navitem'}
                      style={flagged ? { color: 'var(--color-danger, #d32f2f)' } : undefined}
                    >
                      <span className="tl-navitem__mono" aria-hidden="true">{mono}</span>
                      <span className="tl-navitem__label">{it.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
          );
        })}
      </div>
    </nav>
  );
}
